<template>
    <div>
        <div v-if="loading" class="rr-loading"><v-progress-circular indeterminate color="primary" size="26" width="3" /></div>
        <div v-else-if="!rows.length" class="rr-empty">No tags available for this report.</div>
        <div v-else class="rr-matrix-wrap">
            <table class="rr-matrix">
                <thead>
                    <tr>
                        <th class="rr-matrix-tag-col">Tag</th>
                        <th v-for="key in platformKeys" :key="key">
                            <span v-if="platformLogo(key)" class="rr-platform-logo rr-platform-logo--sm" v-html="platformLogo(key)"></span>
                            {{ platformLabel(key) }} · {{ primaryBrandName }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in rows" :key="row.tag">
                        <td class="rr-matrix-tag-col">{{ row.tag }}</td>
                        <td v-for="key in platformKeys" :key="row.tag + key">
                            <div v-if="row.cells[key]" class="rr-cell">
                                <div class="rr-cell-rank" :class="rankClass(row.cells[key].relevanceRank)">
                                    <v-icon v-if="row.cells[key].relevanceRank && row.cells[key].relevanceRank <= 3" x-small>mdi-trophy-outline</v-icon>
                                    {{ rankLabel(row.cells[key].relevanceRank) }}
                                </div>
                                <div class="rr-cell-line">
                                    <span class="rr-cell-key">VIS</span>
                                    <span class="rr-cell-val">{{ row.cells[key].visibility != null ? row.cells[key].visibility.toFixed(2) + "%" : "—" }}</span>
                                </div>
                                <div class="rr-bar rr-bar--cell"><div class="rr-bar-fill" :style="{ width: Math.min(100, row.cells[key].visibility || 0) + '%' }"></div></div>
                                <div class="rr-cell-line">
                                    <span class="rr-cell-key">Sources</span>
                                    <span class="rr-cell-val">{{ row.cells[key].sourceReferences != null ? row.cells[key].sourceReferences : "—" }}</span>
                                </div>
                                <div class="rr-cell-line">
                                    <span class="rr-cell-key">Sent</span>
                                    <span class="rr-cell-val">{{ sentText(row.cells[key].sentiment) }}</span>
                                </div>
                            </div>
                            <div v-else class="rr-empty">—</div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
    import { fetchRangeAverages, fetchTagsList, computeRanges, extractBrandMetricsFromRows, runLimited } from "../reportApi"
    import { getPlatformMeta } from "../../../utils/platformMeta"

    export default {
        name: "TagPerformanceSection",
        props: {
            filters: { type: Object, required: true },
            companyId: { type: String, required: true },
            projectId: { type: String, required: true },
            project: { type: Object, default: null },
            brands: { type: Array, default: () => [] },
            platformKeys: { type: Array, default: () => [] }
        },
        data() {
            return { loading: true, rows: [] }
        },
        computed: {
            primaryBrandName() {
                return this.project?.brand?.brand_name || ""
            }
        },
        methods: {
            platformLabel(key) {
                return getPlatformMeta(key).label || key
            },
            platformLogo(key) {
                return getPlatformMeta(key).logo || null
            },
            rankLabel(rank) {
                if (!rank) return "—"
                const suffix = rank === 1 ? "st" : rank === 2 ? "nd" : rank === 3 ? "rd" : "th"
                return rank + suffix
            },
            rankClass(rank) {
                if (rank === 1) return "rr-rank-cell--1"
                if (rank === 2) return "rr-rank-cell--2"
                if (rank === 3) return "rr-rank-cell--3"
                return "rr-rank-cell--n"
            },
            sentText(sent) {
                if (typeof sent !== "number") return "—"
                const scaled = sent * 100
                return (scaled > 0 ? "+" : "") + scaled.toFixed(0)
            },
            findPrimaryBrand() {
                const mainName = this.primaryBrandName.toLowerCase()
                const list = Array.isArray(this.brands) ? this.brands : []
                return (
                    list.find(
                        (b) =>
                            (b.canonicalName || "").toLowerCase() === mainName ||
                            (Array.isArray(b.mentions) && b.mentions.some((m) => (m || "").toLowerCase() === mainName))
                    ) ||
                    list[0] ||
                    null
                )
            },
            async fetchAll() {
                this.loading = true
                try {
                    const ranges = computeRanges(this.filters.selectedDates)
                    const brand = this.findPrimaryBrand()
                    if (!ranges || !brand || !this.platformKeys.length) {
                        this.rows = []
                        return
                    }
                    // Each row is one topic (tag), each cell = the brand's metrics for that tag alone.
                    // We deliberately do NOT AND-combine the row tag with the report's global tag
                    // filter — that produced confusing "Tag + Tag" rows on the all-platforms matrix.
                    const scopeTag = this.filters.scopeTag || null
                    let tags
                    if (scopeTag) {
                        tags = [scopeTag]
                    } else {
                        const allTags = await fetchTagsList(this.companyId, { projectId: this.projectId, platform: this.filters.platform })
                        tags = allTags.slice(0, 12)
                    }
                    if (!tags.length) {
                        this.rows = []
                        return
                    }
                    const combos = []
                    tags.forEach((tag) => this.platformKeys.forEach((key) => combos.push({ tag, key })))
                    const results = await runLimited(
                        combos.map(({ tag, key }) => async () => {
                            try {
                                const rows = await fetchRangeAverages(this.companyId, {
                                    projectId: this.projectId,
                                    platform: key,
                                    ranges,
                                    brands: this.brands,
                                    tags: [tag],
                                    tagsOperator: "or",
                                    includeSources: true
                                })
                                return extractBrandMetricsFromRows(rows, brand)
                            } catch (e) {
                                return null
                            }
                        }),
                        4
                    )
                    const cellsByTag = {}
                    combos.forEach(({ tag, key }, i) => {
                        if (!cellsByTag[tag]) cellsByTag[tag] = {}
                        cellsByTag[tag][key] = results[i]
                    })
                    this.rows = tags.map((tag) => ({ tag, cells: cellsByTag[tag] || {} }))
                } catch (e) {
                    this.rows = []
                } finally {
                    this.loading = false
                    this.$emit("ready")
                }
            }
        },
        mounted() {
            this.fetchAll()
        }
    }
</script>
